import React from "react";
import { observer } from "mobx-react";
import { CircularProgress } from "@mui/material";
import { fetchFileBlob } from "../../../common/utils/downloadWithAuth";
import { useRootStore } from "../../../store/contexts";
import { ContractFieldReviewErrorBoundary } from "./ContractFieldReviewErrorBoundary";
import { ContractFieldReviewSource } from "./contractFieldReviewSources";

// react-pdf y pdf.js viajan en un chunk aparte: se bajan al primer clic.
const ContractFieldReviewDialog = React.lazy(() =>
  import("./ContractFieldReviewDialog").then((m) => ({
    default: m.ContractFieldReviewDialog,
  })),
);

const sourceKey = (source: ContractFieldReviewSource | null): string | null =>
  source
    ? `${source.entityName}/${source.fieldName}/${source.instanceId}/${source.fileName}`
    : null;

export type ContractFieldReviewHostProps = {
  /** El documento que se revisa; `null` cierra el visor. */
  source: ContractFieldReviewSource | null;
  onClose: () => void;
};

/**
 * El visor de campos de un documento: lo abre el icono «Revisar campos» al lado
 * del nombre de un PDF (ver contractFieldReviewAffordances.tsx). No monta nada
 * hasta la primera apertura, y después queda montado para cerrar con su
 * transición, mostrando el último documento.
 */
export const ContractFieldReviewHost: React.FC<ContractFieldReviewHostProps> =
  observer(({ source, onClose }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;
    const [shown, setShown] = React.useState<ContractFieldReviewSource | null>(
      null,
    );
    // Cada apertura renueva el boundary: después de una falla, abrir de nuevo
    // vuelve a intentar desde cero.
    const [openCount, setOpenCount] = React.useState(0);
    const key = sourceKey(source);

    React.useEffect(() => {
      if (!source) return;
      setShown(source);
      setOpenCount((count) => count + 1);
      // La fuente cuenta por lo que identifica al archivo, no por identidad.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    const loadPdf = React.useCallback(
      () =>
        shown
          ? fetchFileBlob(
              {
                entityName: shown.entityName,
                fieldName: shown.fieldName,
                instanceId: shown.instanceId,
                fileName: shown.fileName,
                localFileName: shown.title,
                inline: true,
              },
              rootStore.activityStore,
            )
          : Promise.resolve(null),
      [shown, rootStore],
    );

    if (!shown) return null;
    const open = source !== null;

    return (
      <ContractFieldReviewErrorBoundary
        key={openCount}
        open={open}
        onClose={onClose}
        langStore={langStore}
      >
        <React.Suspense fallback={<CircularProgress size={18} />}>
          <ContractFieldReviewDialog
            open={open}
            onClose={onClose}
            title={shown.title}
            loadPdf={loadPdf}
          />
        </React.Suspense>
      </ContractFieldReviewErrorBoundary>
    );
  });
